import React, { useCallback } from 'react';
import { useNotifications, useRemoveNotification } from '@/stores/useAppStore';

/**
 * Global notification system powered by Zustand
 * Shows toast notifications for user feedback
 */
const NotificationSystem: React.FC = React.memo(() => {
  const notifications = useNotifications();
  const removeNotification = useRemoveNotification();
  
  const handleRemove = useCallback((id: string) => {
    removeNotification(id);
  }, [removeNotification]);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 w-full max-w-sm sm:max-w-md lg:max-w-lg">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className={`
            w-full shadow-ledge-lg rounded-xl pointer-events-auto
            ring-1 ring-neutral-900 ring-opacity-5 overflow-hidden
            transform transition-all duration-300 ease-in-out
            backdrop-blur-sm
            ${notification.type === 'success' ? 'bg-success-50/95 ring-success-200' : ''}
            ${notification.type === 'error' ? 'bg-error-50/95 ring-error-200' : ''}
            ${notification.type === 'warning' ? 'bg-warning-50/95 ring-warning-200' : ''}
            ${notification.type === 'info' ? 'bg-primary-50/95 ring-primary-200' : ''}
          `}
        >
          <div className="p-4 sm:p-5">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {notification.type === 'success' && (
                  <div className="h-5 w-5 text-success-500">✓</div>
                )}
                {notification.type === 'error' && (
                  <div className="h-5 w-5 text-error-500">✗</div>
                )}
                {notification.type === 'warning' && (
                  <div className="h-5 w-5 text-warning-500">⚠</div>
                )}
                {notification.type === 'info' && (
                  <div className="h-5 w-5 text-primary-500">ℹ</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 break-words">
                  {notification.title}
                </p>
                <p className="mt-1 text-sm text-neutral-600 break-words">
                  {notification.message}
                </p>
              </div>
              <div className="flex-shrink-0 flex">
                <button
                  className="rounded-md inline-flex text-neutral-400 hover:text-neutral-500 focus:outline-none transition-colors"
                  onClick={() => handleRemove(notification.id)}
                >
                  <span className="sr-only">Close</span>
                  <div className="h-5 w-5">×</div>
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

NotificationSystem.displayName = 'NotificationSystem';

export default NotificationSystem;